import { memo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  Input,
  Label,
  FormGroup,
  FormFeedback,
  type InputProps,
} from "reactstrap";

type Props = {
  name: string;
  label?: string;
  type?: InputProps["type"];
  placeholder?: string;
  /** show an inline eye/eye-slash toggle for password inputs */
  withPasswordToggle?: boolean;
  /** optional extra class for the outer FormGroup */
  className?: string;
  labelClassName?: string;
} & Omit<InputProps, "name" | "type" | "invalid" | "id">;

const RHFTextField = memo(function RHFTextField({
  name,
  label,
  type = "text",
  placeholder,
  required = false,
  withPasswordToggle = false,
  className,
  labelClassName,
  ...rest
}: Props) {
  const { control } = useFormContext();
  const [show, setShow] = useState(false);

  const isPassword = type === "password";
  const effectiveType =
    withPasswordToggle && isPassword ? (show ? "text" : "password") : type;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormGroup className={`mb-3 position-relative ${className ?? ""}`}>
          {label && <Label className={labelClassName} htmlFor={name}>{label}</Label>}
          {label && required && (
            <span
              className="ms-1 px-1 py-0 rounded-1 f-10 fw-bold"
              style={{
                background: "#FEE2E2",
                color: "#B91C1C",
                verticalAlign: "middle",
              }}
            >
              required
            </span>
          )}
          <Input
            id={name}
            type={effectiveType}
            placeholder={placeholder}
            invalid={!!fieldState.error}
            disabled={rest.disabled}
            readOnly={rest.readOnly}
            value={
              effectiveType === "time" && field.value
                ? String(field.value).slice(0, 5)
                : (field.value ?? "")
            }
            onChange={(e) => {
              const value = e.target.value;
              field.onChange(
                effectiveType === "time" ? value.slice(0, 5) : value,
              );
            }}
            onBlur={(e) => {
              field.onBlur();
              rest.onBlur?.(e);
            }}
            // add right padding so text doesn't overlap the icon
            style={{
              paddingRight:
                withPasswordToggle && isPassword ? "2.5rem" : undefined,
              ...(rest.style || {}),
            }}
            {...rest}
          />

          {/* Eye / Eye-slash toggle */}
          {withPasswordToggle && isPassword && (
            <button
              type="button"
              aria-label={show ? "Hide password" : "Show password"}
              className="password-toggle-btn"
              onMouseDown={(e) => e.preventDefault()} // keep input focus
              onClick={() => setShow((s) => !s)}
            >
              <i
                className={`text-primary f-18 fa ${show ? "fa-eye-slash" : "fa-eye"}`}
              />
            </button>
          )}

          {fieldState.error && (
            <FormFeedback>{String(fieldState.error.message)}</FormFeedback>
          )}
        </FormGroup>
      )}
    />
  );
});

export default RHFTextField;
